import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, MapPin, Calendar, Car, Star, Users } from 'lucide-react';

const SingaporePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    { icon: <Calendar className="w-6 h-6" />, title: 'Local Events', desc: 'Chinese New Year, Deepavali, Singapore Art Week, Grand Prix and rock concerts featuring international icons.' },
    { icon: <Users className="w-6 h-6" />, title: 'Meet & Greet', desc: "We're happy to provide meet and greet services for the travelling party at Singapore's Seletar Airport." },
    { icon: <Car className="w-6 h-6" />, title: 'Ground Transport', desc: "Ground transportation? We're on it. Seamless arrangements for your entire journey." },
    { icon: <Star className="w-6 h-6" />, title: 'Local Knowledge', desc: "We've plenty of recommendations and insider tips for your Singapore visit." },
  ];

  return (
    <div data-testid="singapore-page" className="bg-white">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        {/* Desktop */}
        <div className="absolute inset-0 hidden md:block">
          <img
            src="https://images.unsplash.com/photo-1574227492706-f65b24c3688a?q=80&w=2832&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            alt="Singapore skyline with private jet"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/40 to-transparent"></div>
        </div>
        {/* Mobile */}
        <div className="absolute inset-0 md:hidden">
          <img
            src="https://images.unsplash.com/photo-1574227492706-f65b24c3688a?q=80&w=2832&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            alt="Singapore skyline with private jet"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/40 to-transparent"></div>
        </div>

        <div className="relative z-10 container-padding w-full pt-32 pb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block text-blue-700 text-sm font-medium tracking-widest uppercase mb-6">
              Our Home
            </span>
            <h1 
              data-testid="singapore-headline"
              className="text-4xl sm:text-5xl lg:text-6xl font-medium text-white leading-tight mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Singapore
            </h1>
            <p className="text-lg text-white/80 font-light leading-relaxed max-w-2xl mb-8">
              Our home is Singapore and we think we know it best!
            </p>
            <Link
              to="/contact"
              data-testid="hero-enquiry-btn"
              className="inline-flex items-center justify-center gap-2 bg-red-600 text-white px-8 py-4 text-sm font-medium tracking-wider uppercase hover:bg-red-700 transition-all duration-300"
            >
              Send Enquiry
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-gap">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <span className="caption-text text-blue-700 mb-4 block">Singaporean with Global Reach</span>
              <h2 
                className="heading-lg text-slate-900 mb-6"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                Your Gateway to Southeast Asia
              </h2>
              <div className="gold-line mb-8"></div>
              <p className="body-large mb-6">
                Singapore Air Charter can secure an aircraft perfectly suited to your requirements here in Southeast Asia.
              </p>
              <p className="body-default mb-6">
                We've intimate knowledge of local events from cultural and festive highlights such as Chinese New Year, Deepavali and Singapore Art Week to our super exciting major entertainment events like the Singapore Grand Prix and rock concerts featuring international icons.
              </p>
              <p className="body-default mb-6">
                And we know how to get you here! Meet and greet? No problem. We're happy to provide 'meet and greet' services for the travelling party at Singapore's Seletar Airport. Ground transportation? We're on it. Local knowledge and recommendations? We've plenty of those!
              </p>
              <p className="body-default mb-8">
                Whatever draws you to our island home, please send us your enquiry and we'll make it happen!
              </p>
              <p className="text-lg font-medium text-slate-900 italic" style={{ fontFamily: 'Playfair Display, serif' }}>
                Singapore Air Charter — we're all things Singapore when it comes to local knowledge and private air travel.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img
                src="/gp1.jpg"
                alt="Marina Bay Sands Singapore"
                className="w-full h-[500px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Highlights */}
      <section className="section-gap bg-slate-50">
        <div className="container-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="caption-text text-blue-700 mb-4 block">What We Offer</span>
            <h2 
              className="heading-lg text-slate-900 mb-4"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Singapore Services
            </h2>
            <div className="gold-line mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-8 text-center border border-slate-100"
              >
                <div className="text-blue-700 mb-4 flex justify-center">{item.icon}</div>
                <h3 className="text-lg font-medium text-slate-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-gap">
        <div className="container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 
              className="heading-lg text-slate-900 mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Planning a Trip to Singapore?
            </h2>
            <p className="body-large mb-10 max-w-2xl mx-auto">
              Let us handle your private aviation needs and help you experience Singapore like never before.
            </p>
            <Link
              to="/contact"
              data-testid="final-cta-btn"
              className="inline-flex items-center justify-center gap-2 bg-red-600 text-white px-10 py-4 text-sm font-medium tracking-wider uppercase hover:bg-red-700 transition-all duration-300"
            >
              Send Enquiry
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default SingaporePage;
